import { motion } from 'framer-motion';
import {
  Building,
  FileText,
  Globe,
  Image,
  Mail,
  MapPin,
  Phone,
  Save,
  Upload,
  Users,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { employerAPI } from '../utils/api';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
};

const Settings = () => {
  // State for settings tabs
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // State for form data
  const [companyData, setCompanyData] = useState({
    companyName: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    industry: '',
    size: '',
    description: '',
    logo: null,
  });

  // State for notifications settings
  const [notifications, setNotifications] = useState({
    newApplications: true,
    interviewReminders: true,
    candidateUpdates: true,
    systemUpdates: false,
    marketingEmails: false,
  });

  // Fetch employer profile on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await employerAPI.getProfile();
        const profile = response.data.data;
        
        setCompanyData({
          companyName: profile.companyName || '',
          website: profile.website || '',
          email: profile.email || '',
          phone: profile.phone || '',
          address: profile.address || '',
          industry: profile.industry || '',
          size: profile.size || '',
          description: profile.description || '',
          logo: profile.logo || null,
        });
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle company data changes
  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setCompanyData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle notification toggles
  const handleNotificationToggle = (setting) => {
    setNotifications((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      
      const formData = new FormData();
      formData.append('logo', file);

      const response = await employerAPI.uploadLogo(formData);
      
      if (response.data.success) {
        setCompanyData((prev) => ({
          ...prev,
          logo: response.data.data.logo,
        }));
        toast.success('Logo uploaded successfully!');
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset input
      e.target.value = '';
    }
  };

  // Remove logo
  const handleLogoRemove = () => {
    setCompanyData((prev) => ({
      ...prev,
      logo: null,
    }));
    toast.success('Logo removed. Don\'t forget to save changes.');
  };

  // Handle save settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      // Prepare data for API (exclude logo and email as they're handled separately)
      const updateData = {
        companyName: companyData.companyName,
        phone: companyData.phone,
        website: companyData.website,
        description: companyData.description,
        address: companyData.address,
        industry: companyData.industry,
        size: companyData.size,
      };
      
      await employerAPI.updateProfile(updateData);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to save profile changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner w-8 h-8 border-4"></div>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page header */}
      <motion.div variants={itemVariants}>
        <h1 className="page-title">Settings</h1>
        <p className="text-gray-600 mt-1">
          Manage your company profile and preferences
        </p>
      </motion.div>

      {/* Settings tabs and content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings content */}
        <motion.div className="lg:col-span-3" variants={itemVariants}>
          <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
            {activeTab === 'company' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 font-heading">Company Profile</h2>
                <p className="text-sm text-gray-600">
                  Update your company information that will be shown on job postings and candidate communications.
                </p>

                {/* Logo Upload Section */}
                <div>
                  <label className="form-label">Company Logo</label>
                  <div className="space-y-4">
                    {/* Current Logo Display */}
                    {companyData.logo && companyData.logo.url ? (
                      <div className="relative inline-block">
                        <img
                          src={`${process.env.REACT_APP_API_URL}${companyData.logo.url}`}
                          alt="Company Logo"
                          className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={handleLogoRemove}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                          title="Remove logo"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                          <Image size={24} className="mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500">No logo</p>
                        </div>
                      </div>
                    )}

                    {/* Upload Button */}
                    <div>
                      <input
                        type="file"
                        id="logoUpload"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      <label
                        htmlFor="logoUpload"
                        className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 cursor-pointer transition-colors ${
                          uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {uploadingLogo ? (
                          <>
                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={16} className="mr-2" />
                            {companyData.logo ? 'Change Logo' : 'Upload Logo'}
                          </>
                        )}
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        JPG, PNG, GIF up to 5MB. Recommended: 200x200px
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Company Name</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Building size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="companyName"
                        value={companyData.companyName}
                        onChange={handleCompanyChange}
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="form-label">Company Website</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Globe size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="url"
                        name="website"
                        value={companyData.website}
                        onChange={handleCompanyChange}
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="form-label">Email Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={companyData.email}
                        readOnly
                        className="input-field pl-10 bg-gray-50"
                        title="Email cannot be changed"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Email address cannot be changed</p>
                  </div>
                  
                  <div>
                    <label className="form-label">Phone Number</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        value={companyData.phone}
                        onChange={handleCompanyChange}
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="form-label">Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPin size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="address"
                        value={companyData.address}
                        onChange={handleCompanyChange}
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="form-label">Industry</label>
                    <select
                      name="industry"
                      value={companyData.industry}
                      onChange={handleCompanyChange}
                      className="select-field"
                    >
                      <option value="">Select Industry</option>
                      <option value="Technology">Technology</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Finance">Finance</option>
                      <option value="Education">Education</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Retail">Retail</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="form-label">Company Size</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Users size={16} className="text-gray-400" />
                      </div>
                      <select
                        name="size"
                        value={companyData.size}
                        onChange={handleCompanyChange}
                        className="select-field pl-10"
                      >
                        <option value="">Select Company Size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="50-100">50-100 employees</option>
                        <option value="100-500">100-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="form-label">Company Description</label>
                    <div className="relative">
                      <div className="absolute top-3 left-3 flex items-center pointer-events-none">
                        <FileText size={16} className="text-gray-400" />
                      </div>
                      <textarea
                        name="description"
                        value={companyData.description}
                        onChange={handleCompanyChange}
                        rows={4}
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="pt-4">
                  <button 
                    onClick={handleSaveSettings}
                    disabled={saving || uploadingLogo}
                    className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="loading-spinner mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} className="mr-2" />
                        Save Company Profile
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Settings; 